import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  FaCog,
  FaComments,
  FaDownload,
  FaEdit,
  FaBuilding,
  FaClinicMedical,
  FaFileSignature,
  FaHome,
  FaList,
  FaBullhorn,
  FaPhoneAlt,
  FaPlus,
  FaRocket,
  FaStar,
  FaThLarge,
  FaTimesCircle,
  FaTrash,
  FaTrophy,
  FaUserCheck,
  FaUserClock,
  FaCalendarCheck,
  FaHandshake,
  FaBan,
  FaQuestionCircle,
  FaUserPlus,
  FaChevronUp,
  FaChevronDown,
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import MauticConfig from '../components/MauticConfig';
import { Button } from 'components/ui/button';

const DEFAULT_PIPELINE_STAGES = [
  { key: 'Nuevo', probability: 10, iconKey: 'user-plus' },
  { key: 'En contacto', probability: 25, iconKey: 'phone' },
  { key: 'Potencial cliente', probability: 50, iconKey: 'user-check' },
  { key: 'Propuesta enviada', probability: 75, iconKey: 'file-sign' },
  { key: 'Ganado', probability: 100, iconKey: 'trophy' },
  { key: 'Perdido', probability: 0, iconKey: 'times-circle' },
];

const ICONS = {
  'user-plus': FaUserPlus,
  phone: FaPhoneAlt,
  'user-check': FaUserCheck,
  'file-sign': FaFileSignature,
  trophy: FaTrophy,
  'times-circle': FaTimesCircle,
  'user-clock': FaUserClock,
  'calendar-check': FaCalendarCheck,
  handshake: FaHandshake,
  ban: FaBan,
};

const ICON_ALIASES = {
  'fa-user-plus': 'user-plus',
  'phone-alt': 'phone',
  'phone-call': 'phone',
  'user-checked': 'user-check',
  'file-signature': 'file-sign',
  won: 'trophy',
  lost: 'times-circle',
  cancel: 'times-circle',
  cancelled: 'times-circle',
  canceled: 'times-circle',
  close: 'times-circle',
  'x-circle': 'times-circle',
  'user-time': 'user-clock',
  meeting: 'calendar-check',
  'check-circle': 'user-check',
  'calendar-alt': 'calendar-check',
  'calendar': 'calendar-check',
  deal: 'handshake',
  'hand-shake': 'handshake',
  archive: 'ban',
  rejected: 'ban',
};

const ICON_OPTIONS = [
  { value: 'user-plus', label: 'Nuevo contacto' },
  { value: 'phone', label: 'Contacto' },
  { value: 'user-check', label: 'Calificado' },
  { value: 'user-clock', label: 'Seguimiento' },
  { value: 'file-sign', label: 'Propuesta' },
  { value: 'calendar-check', label: 'Cita agendada' },
  { value: 'handshake', label: 'Negociación' },
  { value: 'trophy', label: 'Ganado' },
  { value: 'ban', label: 'Descartado' },
  { value: 'times-circle', label: 'Perdido' },
];

const BUSINESS_PIPELINE_PRESETS = [
  {
    id: 'general',
    name: 'General',
    description: 'Pipeline estándar multiuso.',
    icon: FaBuilding,
    stages: [
      { key: 'Nuevo', probability: 10, iconKey: 'user-plus' },
      { key: 'En contacto', probability: 25, iconKey: 'phone' },
      { key: 'Potencial cliente', probability: 50, iconKey: 'user-check' },
      { key: 'Propuesta enviada', probability: 75, iconKey: 'file-sign' },
      { key: 'Ganado', probability: 100, iconKey: 'trophy' },
      { key: 'Perdido', probability: 0, iconKey: 'times-circle' },
    ],
  },
  {
    id: 'clinic',
    name: 'Clínica / Salud',
    description: 'Captación y cierre de pacientes.',
    icon: FaClinicMedical,
    stages: [
      { key: 'Nuevo lead', probability: 10, iconKey: 'user-plus' },
      { key: 'Primer contacto', probability: 25, iconKey: 'phone' },
      { key: 'Evaluación realizada', probability: 45, iconKey: 'user-check' },
      { key: 'Cita agendada', probability: 70, iconKey: 'calendar-check' },
      { key: 'Tratamiento confirmado', probability: 100, iconKey: 'trophy' },
      { key: 'No interesado', probability: 0, iconKey: 'times-circle' },
    ],
  },
  {
    id: 'real-estate',
    name: 'Inmobiliaria',
    description: 'Desde lead hasta firma de operación.',
    icon: FaHome,
    stages: [
      { key: 'Lead nuevo', probability: 10, iconKey: 'user-plus' },
      { key: 'Contacto validado', probability: 30, iconKey: 'phone' },
      { key: 'Visita programada', probability: 50, iconKey: 'calendar-check' },
      { key: 'Oferta enviada', probability: 75, iconKey: 'file-sign' },
      { key: 'Operación cerrada', probability: 100, iconKey: 'trophy' },
      { key: 'Descartado', probability: 0, iconKey: 'ban' },
    ],
  },
  {
    id: 'marketing-agency',
    name: 'Agencia de Marketing',
    description: 'Proceso comercial para servicios recurrentes.',
    icon: FaBullhorn,
    stages: [
      { key: 'Lead captado', probability: 10, iconKey: 'user-plus' },
      { key: 'Discovery call', probability: 30, iconKey: 'phone' },
      { key: 'Brief validado', probability: 50, iconKey: 'user-check' },
      { key: 'Propuesta enviada', probability: 75, iconKey: 'file-sign' },
      { key: 'Cliente activo', probability: 100, iconKey: 'trophy' },
      { key: 'No encaja', probability: 0, iconKey: 'times-circle' },
    ],
  },
  {
    id: 'saas',
    name: 'SaaS / Tecnología',
    description: 'Venta consultiva con demo y trial.',
    icon: FaRocket,
    stages: [
      { key: 'Lead inbound', probability: 10, iconKey: 'user-plus' },
      { key: 'Calificación', probability: 25, iconKey: 'user-check' },
      { key: 'Demo realizada', probability: 55, iconKey: 'calendar-check' },
      { key: 'Negociación', probability: 80, iconKey: 'handshake' },
      { key: 'Cuenta ganada', probability: 100, iconKey: 'trophy' },
      { key: 'Churn / perdido', probability: 0, iconKey: 'times-circle' },
    ],
  },
];

function safeText(value) {
  return (value || '').toString();
}

function getStoredToken() {
  return sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');
}

function parseLeadNote(rawNote) {
  try {
    const outer = typeof rawNote === 'string' ? JSON.parse(rawNote) : rawNote;
    const nested = typeof outer?.content === 'string' ? JSON.parse(outer.content) : outer;
    return {
      content: nested?.content || outer?.content || 'Nota sin contenido',
      timestamp: nested?.timestamp || outer?.timestamp || null,
    };
  } catch (error) {
    return { content: 'Error al leer nota', timestamp: null };
  }
}

function scoreBadgeClass(score) {
  if (score >= 80) return 'bg-emerald-100 text-emerald-700';
  if (score >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
}

function normalizePipelineStages(stages) {
  if (!Array.isArray(stages) || stages.length === 0) return DEFAULT_PIPELINE_STAGES;
  const normalized = stages
    .map((stage, idx) => ({
      key: safeText(stage?.key).trim() || `Fase ${idx + 1}`,
      probability: Number.isFinite(Number(stage?.probability)) ? Math.max(0, Math.min(100, Math.round(Number(stage.probability)))) : 0,
      iconKey: (() => {
        const rawIconKey = safeText(stage?.iconKey).trim().toLowerCase();
        const normalizedIconKey = ICON_ALIASES[rawIconKey] || rawIconKey;
        return ICONS[normalizedIconKey] ? normalizedIconKey : 'user-check';
      })(),
    }))
    .filter((stage) => stage.key.length > 0);
  return normalized.length > 0 ? normalized : DEFAULT_PIPELINE_STAGES;
}

function stagePalette(index) {
  const palettes = [
    { chip: 'bg-slate-100 text-slate-700', column: 'bg-slate-50 border-slate-200' },
    { chip: 'bg-blue-100 text-blue-700', column: 'bg-blue-50 border-blue-200' },
    { chip: 'bg-indigo-100 text-indigo-700', column: 'bg-indigo-50 border-indigo-200' },
    { chip: 'bg-amber-100 text-amber-700', column: 'bg-amber-50 border-amber-200' },
    { chip: 'bg-emerald-100 text-emerald-700', column: 'bg-emerald-50 border-emerald-200' },
    { chip: 'bg-rose-100 text-rose-700', column: 'bg-rose-50 border-rose-200' },
  ];
  return palettes[index % palettes.length];
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const [pipelineStages, setPipelineStages] = useState(DEFAULT_PIPELINE_STAGES);
  const [pipelineDraft, setPipelineDraft] = useState(DEFAULT_PIPELINE_STAGES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [appointmentFilter, setAppointmentFilter] = useState('todos');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [showMauticConfig, setShowMauticConfig] = useState(false);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [showEditLead, setShowEditLead] = useState(false);
  const [showPipelineConfig, setShowPipelineConfig] = useState(false);
  const [editLeadDraft, setEditLeadDraft] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('general');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [newLead, setNewLead] = useState({ nombre: '', email: '', telefono: '', estado: DEFAULT_PIPELINE_STAGES[0].key });
  const [kanbanColumns, setKanbanColumns] = useState({});
  const pipelineStagesRef = useRef(DEFAULT_PIPELINE_STAGES);
  const initializedRef = useRef(false);

  const stageKeys = useMemo(() => pipelineStages.map((stage) => stage.key), [pipelineStages]);
  const stageByKey = useMemo(() => pipelineStages.reduce((acc, stage) => ({ ...acc, [stage.key]: stage }), {}), [pipelineStages]);
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${getStoredToken()}` } }), []);

  useEffect(() => {
    pipelineStagesRef.current = pipelineStages;
  }, [pipelineStages]);

  const normalizeLeadStatus = useCallback(
    (status, stagesInput) => {
      const stages = normalizePipelineStages(stagesInput || pipelineStagesRef.current);
      const stageKeysLocal = stages.map((stage) => stage.key);
      const raw = safeText(status).trim();
      const lowered = raw.toLowerCase();
      const legacyMap = {
        'no contactado': 'Nuevo',
        contactado: 'En contacto',
        precalificado: 'Potencial cliente',
        'en negociación': 'Propuesta enviada',
        cerrado: 'Ganado',
        nuevo: 'Nuevo',
        perdido: 'Perdido',
      };
      const mapped = legacyMap[lowered] || raw;
      return stageKeysLocal.includes(mapped) ? mapped : stageKeysLocal[0];
    },
    []
  );

  const getStageProbability = useCallback(
    (status) => stageByKey[normalizeLeadStatus(status)]?.probability ?? 0,
    [stageByKey, normalizeLeadStatus]
  );

  const computeLeadScore = useCallback(
    (lead) => {
      const status = normalizeLeadStatus(lead?.estado);
      let score = getStageProbability(status);
      if (status.toLowerCase().includes('perd')) return 0;

      const totalCitas = Number(lead?.total_citas || 0);
      const notas = Array.isArray(lead?.notas) ? lead.notas.length : 0;
      const hasEmail = safeText(lead?.email) && safeText(lead?.email) !== 'Sin email';
      const hasPhone = safeText(lead?.telefono) && safeText(lead?.telefono) !== 'Sin teléfono';
      const days = Number(lead?.dias_desde_ultima_cita);

      score += Math.min(15, totalCitas * 5);
      if (lead?.tiene_cita) score += 8;
      score += Math.min(8, notas * 2);
      if (hasEmail && hasPhone) score += 6;
      else if (hasEmail || hasPhone) score += 3;
      if (Number.isFinite(days)) {
        if (days <= 7) score += 6;
        else if (days <= 30) score += 3;
      }
      if (status.toLowerCase().includes('ganad')) score = Math.max(score, 90);
      return Math.max(0, Math.min(100, Math.round(score)));
    },
    [getStageProbability, normalizeLeadStatus]
  );

  const updateKanbanColumns = useCallback(
    (leadsData, stagesInput) => {
      const stages = normalizePipelineStages(stagesInput || pipelineStagesRef.current);
      const cols = stages.reduce((acc, stage) => ({ ...acc, [stage.key]: [] }), {});
      leadsData.forEach((lead) => {
        const key = stages.some((stage) => stage.key === lead.estado) ? lead.estado : stages[0].key;
        cols[key].push(lead);
      });
      setKanbanColumns(cols);
    },
    []
  );

  const fetchPipelineConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/leads/pipeline-config', authHeaders);
      const stages = normalizePipelineStages(response.data?.stages);
      setPipelineStages(stages);
      setPipelineDraft(stages);
      return stages;
    } catch (err) {
      setPipelineStages(DEFAULT_PIPELINE_STAGES);
      setPipelineDraft(DEFAULT_PIPELINE_STAGES);
      return DEFAULT_PIPELINE_STAGES;
    }
  }, [authHeaders]);

  const fetchLeads = useCallback(
    async (stagesInput, options = {}) => {
      try {
        const silent = !!options.silent;
        if (!silent) setLoading(true);
        const token = getStoredToken();
        if (!token) {
          setError('No se encontró el token de autenticación. Inicia sesión de nuevo.');
          if (!silent) setLoading(false);
          return [];
        }
        const response = await axios.get('/api/leads', { headers: { Authorization: `Bearer ${token}` } });
        const stages = normalizePipelineStages(stagesInput || pipelineStagesRef.current);
        const stageSet = new Set(stages.map((s) => s.key));
        const fallback = stages[0].key;
        const normalized = (response.data || []).map((lead) => ({
          ...lead,
          estado: stageSet.has(normalizeLeadStatus(lead.estado, stages)) ? normalizeLeadStatus(lead.estado, stages) : fallback,
          notas: Array.isArray(lead.notas) ? lead.notas : lead.notas ? [lead.notas] : [],
        }));
        setLeads(normalized);
        updateKanbanColumns(normalized, stages);
        setError('');
        return normalized;
      } catch (err) {
        setError(`Error al cargar leads: ${err.response?.data?.error || err.message}`);
        return [];
      } finally {
        if (!options.silent) setLoading(false);
      }
    },
    [normalizeLeadStatus, updateKanbanColumns]
  );

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const init = async () => {
      const stages = await fetchPipelineConfig();
      await fetchLeads(stages);
      setNewLead((prev) => ({ ...prev, estado: stages[0].key }));
    };
    init();
  }, [fetchPipelineConfig, fetchLeads]);

  const filteredLeads = useMemo(() => {
    const text = searchTerm.toLowerCase().trim();
    return leads.filter((lead) => {
      const matchesText =
        safeText(lead.nombre).toLowerCase().includes(text) ||
        safeText(lead.email).toLowerCase().includes(text) ||
        safeText(lead.telefono).toLowerCase().includes(text);
      const matchesStatus = statusFilter === 'Todos' || normalizeLeadStatus(lead.estado) === statusFilter;
      const totalCitas = Number(lead.total_citas || 0);
      const matchesAppointments =
        appointmentFilter === 'todos' ||
        (appointmentFilter === 'con_citas' && totalCitas > 0) ||
        (appointmentFilter === 'sin_citas' && totalCitas === 0);
      const leadDate = lead.ultima_cita || lead.fecha_creacion || null;
      const leadDateObj = leadDate ? new Date(leadDate) : null;
      const fromOk = !dateFrom || (leadDateObj && leadDateObj >= new Date(`${dateFrom}T00:00:00`));
      const toOk = !dateTo || (leadDateObj && leadDateObj <= new Date(`${dateTo}T23:59:59`));
      return matchesText && matchesStatus && matchesAppointments && fromOk && toOk;
    });
  }, [leads, searchTerm, statusFilter, appointmentFilter, dateFrom, dateTo, normalizeLeadStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const paginatedLeads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLeads.slice(start, start + pageSize);
  }, [filteredLeads, page, pageSize]);

  useEffect(() => setPage(1), [searchTerm, statusFilter, appointmentFilter, dateFrom, dateTo, pageSize]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const stats = useMemo(() => {
    const total = leads.length;
    const closed = leads.filter((lead) => {
      const s = normalizeLeadStatus(lead.estado).toLowerCase();
      return s.includes('ganad') || s.includes('perd');
    }).length;
    const withAppointments = leads.filter((lead) => Number(lead.total_citas || 0) > 0).length;
    const avgAppointments = total ? leads.reduce((sum, lead) => sum + Number(lead.total_citas || 0), 0) / total : 0;
    const avgScore = total ? leads.reduce((sum, lead) => sum + computeLeadScore(lead), 0) / total : 0;
    return { total, closed, withAppointments, avgAppointments, avgScore };
  }, [leads, computeLeadScore, normalizeLeadStatus]);

  const statusCounts = useMemo(() => {
    return stageKeys.reduce((acc, key) => {
      acc[key] = leads.filter((lead) => normalizeLeadStatus(lead.estado) === key).length;
      return acc;
    }, {});
  }, [stageKeys, leads, normalizeLeadStatus]);

  const updateLeadEstado = useCallback(
    async (lead, newStatus) => {
      if (!lead?.id || !newStatus || normalizeLeadStatus(lead.estado) === newStatus) return;
      const previousLeads = leads;
      const updated = leads.map((item) => (item.id === lead.id ? { ...item, estado: newStatus } : item));
      setLeads(updated);
      updateKanbanColumns(updated);
      try {
        await axios.put(
          `/api/leads/${lead.id}`,
          {
            nombre: lead.nombre,
            email: lead.email === 'Sin email' ? null : lead.email,
            telefono: lead.telefono === 'Sin teléfono' ? null : lead.telefono,
            estado: newStatus,
          },
          authHeaders
        );
      } catch (err) {
        setLeads(previousLeads);
        updateKanbanColumns(previousLeads);
        setError(`No se pudo actualizar el estado: ${err.response?.data?.error || err.message}`);
      }
    },
    [authHeaders, leads, updateKanbanColumns, normalizeLeadStatus]
  );

  const onDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;
      const { source, destination, draggableId } = result;
      if (source.droppableId === destination.droppableId) return;
      const dragged = leads.find((lead) => String(lead.id) === String(draggableId));
      if (!dragged) return;
      await updateLeadEstado(dragged, destination.droppableId);
    },
    [leads, updateLeadEstado]
  );

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get('/api/leads/download', { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(`Error al descargar CSV: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('¿Eliminar este lead? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`/api/leads/${leadId}`, authHeaders);
      const updated = leads.filter((lead) => lead.id !== leadId);
      setLeads(updated);
      updateKanbanColumns(updated);
      if (selectedLead?.id === leadId) setSelectedLead(null);
      setError('');
    } catch (err) {
      setError(`Error al eliminar el lead: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCreateLead = async () => {
    if (!newLead.nombre.trim()) {
      setError('El nombre es obligatorio para crear el lead.');
      return;
    }
    try {
      await axios.post(
        '/api/leads',
        {
          nombre: newLead.nombre.trim(),
          email: newLead.email.trim() || null,
          telefono: newLead.telefono.trim() || null,
          estado: newLead.estado,
        },
        authHeaders
      );
      setShowCreateLead(false);
      setNewLead({ nombre: '', email: '', telefono: '', estado: stageKeys[0] || 'Nuevo' });
      await fetchLeads(undefined, { silent: true });
    } catch (err) {
      setError(`Error al crear lead: ${err.response?.data?.error || err.message}`);
    }
  };

  const openEditLead = (lead) => {
    setEditLeadDraft({
      id: lead.id,
      nombre: lead.nombre || '',
      email: lead.email === 'Sin email' ? '' : lead.email || '',
      telefono: lead.telefono === 'Sin teléfono' ? '' : lead.telefono || '',
      estado: normalizeLeadStatus(lead.estado),
    });
    setShowEditLead(true);
  };

  const handleSaveLeadEdit = async () => {
    if (!editLeadDraft?.id) return;
    if (!safeText(editLeadDraft.nombre).trim()) {
      setError('El nombre no puede quedar vacío.');
      return;
    }
    try {
      await axios.put(
        `/api/leads/${editLeadDraft.id}`,
        {
          nombre: editLeadDraft.nombre.trim(),
          email: safeText(editLeadDraft.email).trim() || null,
          telefono: safeText(editLeadDraft.telefono).trim() || null,
          estado: editLeadDraft.estado,
        },
        authHeaders
      );
      setShowEditLead(false);
      setEditLeadDraft(null);
      const refreshed = await fetchLeads(undefined, { silent: true });
      if (selectedLead?.id) {
        const updatedSelected = refreshed.find((lead) => lead.id === selectedLead.id);
        if (updatedSelected) setSelectedLead(updatedSelected);
      }
    } catch (err) {
      setError(`Error al guardar lead: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleAddNote = async () => {
    if (!selectedLead?.id || !newNote.trim()) return;
    try {
      const innerNote = JSON.stringify({ content: newNote.trim(), timestamp: new Date().toISOString() });
      const payload = JSON.stringify({ content: innerNote, timestamp: new Date().toISOString() });
      await axios.post(`/api/leads/${selectedLead.id}/notes`, { nota: payload }, authHeaders);
      setNewNote('');
      const refreshedLeads = await fetchLeads(undefined, { silent: true });
      const refreshed = refreshedLeads.find((lead) => lead.id === selectedLead.id);
      if (refreshed) setSelectedLead(refreshed);
    } catch (err) {
      setError(`Error al añadir nota: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeleteNote = async (noteIndex) => {
    if (!selectedLead?.id) return;
    try {
      await axios.delete(`/api/leads/${selectedLead.id}/notes/${noteIndex}`, authHeaders);
      const refreshedLeads = await fetchLeads(undefined, { silent: true });
      const refreshed = refreshedLeads.find((lead) => lead.id === selectedLead.id);
      if (refreshed) setSelectedLead(refreshed);
    } catch (err) {
      setError(`Error al eliminar nota: ${err.response?.data?.error || err.message}`);
    }
  };

  const movePipelineStage = (index, dir) => {
    const next = [...pipelineDraft];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    setPipelineDraft(next);
  };

  const savePipelineConfig = async () => {
    try {
      const normalized = normalizePipelineStages(pipelineDraft);
      await axios.put('/api/leads/pipeline-config', { stages: normalized }, authHeaders);
      setPipelineStages(normalized);
      setPipelineDraft(normalized);
      setShowPipelineConfig(false);
      const refreshed = await fetchLeads(normalized, { silent: true });
      if (selectedLead?.id) {
        const updatedSelected = refreshed.find((lead) => lead.id === selectedLead.id);
        if (updatedSelected) setSelectedLead(updatedSelected);
      }
      setError('');
    } catch (err) {
      setError(`Error al guardar pipeline: ${err.response?.data?.error || err.message}`);
    }
  };

  const applyBusinessPreset = (presetId) => {
    const preset = BUSINESS_PIPELINE_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    const normalized = normalizePipelineStages(preset.stages);
    setSelectedPreset(presetId);
    setPipelineDraft(normalized);
  };

  return (
    <div className="w-full px-3 md:px-5 pb-8">
      <div className="mb-5 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">Leads</h2>
            <p className="text-sm text-gray-500">Pipeline configurable por negocio: fases, probabilidad y scoring automático.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowCreateLead(true)} variant="primary" className="shadow-sm"><FaPlus className="mr-2" /> Nuevo lead</Button>
            <button
              type="button"
              onClick={() => setShowPipelineConfig(true)}
              className="inline-flex h-10 items-center justify-center rounded-lg bg-violet-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
              style={{
                opacity: 1,
                position: 'relative',
                zIndex: 30,
                filter: 'none',
                backgroundColor: '#7c3aed',
                color: '#ffffff',
                border: '1px solid #6d28d9',
              }}
            >
              <FaCog className="mr-2" />
              Pipeline
            </button>
            <Button onClick={() => setShowMauticConfig(true)} className="bg-gray-700 text-white shadow-sm hover:bg-gray-800"><FaCog className="mr-2" /> Mautic</Button>
            <Button onClick={() => setViewMode(viewMode === 'list' ? 'kanban' : 'list')} className="bg-indigo-600 text-white shadow-sm hover:bg-indigo-700">{viewMode === 'list' ? <><FaThLarge className="mr-2" /> Kanban</> : <><FaList className="mr-2" /> Lista</>}</Button>
            <button
              type="button"
              onClick={handleDownloadCSV}
              className="inline-flex h-10 items-center justify-center rounded-lg bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              style={{
                opacity: 1,
                position: 'relative',
                zIndex: 30,
                filter: 'none',
                backgroundColor: '#059669',
                color: '#ffffff',
                border: '1px solid #047857',
              }}
            >
              <FaDownload className="mr-2" />
              CSV
            </button>
          </div>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total leads</p><p className="mt-2 text-3xl font-bold text-gray-800">{stats.total}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Con citas</p><p className="mt-2 text-3xl font-bold text-blue-700">{stats.withAppointments}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Cerrados</p><p className="mt-2 text-3xl font-bold text-emerald-700">{stats.closed}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Citas promedio / lead</p><p className="mt-2 text-3xl font-bold text-indigo-700">{stats.avgAppointments.toFixed(1)}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Score promedio</p><p className="mt-2 text-3xl font-bold text-fuchsia-700">{stats.avgScore.toFixed(0)}</p></div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {pipelineStages.map((stage, index) => {
          const Icon = ICONS[stage.iconKey] || FaUserCheck;
          const palette = stagePalette(index);
          return (
            <div key={stage.key} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`rounded-md p-2 ${palette.column}`}><Icon className="text-slate-900" /></span>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{stage.key}</p>
                    <p className="text-xs text-gray-500">Probabilidad: {stage.probability}%</p>
                  </div>
                </div>
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">{statusCounts[stage.key] || 0}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, email o teléfono" className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
            <option value="Todos">Todos los estados</option>
            {stageKeys.map((status) => <option key={status} value={status}>{status} ({statusCounts[status] || 0})</option>)}
          </select>
          <select value={appointmentFilter} onChange={(e) => setAppointmentFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
            <option value="todos">Con y sin citas</option>
            <option value="con_citas">Solo con citas</option>
            <option value="sin_citas">Solo sin citas</option>
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          <button onClick={() => { setSearchTerm(''); setStatusFilter('Todos'); setAppointmentFilter('todos'); setDateFrom(''); setDateTo(''); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Limpiar filtros</button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-500">Cargando leads...</div>
      ) : viewMode === 'list' ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Lead</th>
                  <th className="px-4 py-3 text-left font-semibold">Contacto</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                  <th className="px-4 py-3 text-left font-semibold">Score</th>
                  <th className="px-4 py-3 text-left font-semibold">Última cita</th>
                  <th className="px-4 py-3 text-left font-semibold">Total citas</th>
                  <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center text-gray-500" colSpan={7}>No hay leads con los filtros actuales.</td></tr>
                ) : (
                  paginatedLeads.map((lead) => {
                    const normalizedStatus = normalizeLeadStatus(lead.estado);
                    const score = computeLeadScore(lead);
                    const stage = stageByKey[normalizedStatus];
                    const stageIndex = Math.max(0, pipelineStages.findIndex((s) => s.key === normalizedStatus));
                    const palette = stagePalette(stageIndex);
                    return (
                      <tr key={lead.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                        <td className="px-4 py-3"><p className="font-semibold text-gray-800">{lead.nombre || 'Sin nombre'}</p><p className="text-xs text-gray-500">{lead.ultimo_servicio || 'Sin servicio registrado'}</p></td>
                        <td className="px-4 py-3"><p className="text-gray-700">{lead.email || 'Sin email'}</p><p className="text-xs text-gray-500">{lead.telefono || 'Sin teléfono'}</p></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${palette.chip}`}>{normalizedStatus}</span>
                            <select value={normalizedStatus} onChange={(e) => updateLeadEstado(lead, e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-xs">
                              {stageKeys.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                            <span className="text-xs text-gray-500">{stage?.probability || 0}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${scoreBadgeClass(score)}`}><FaStar className="mr-1" />{score}</span></td>
                        <td className="px-4 py-3 text-gray-700">{lead.ultima_cita ? new Date(lead.ultima_cita).toLocaleDateString() : 'Sin citas'}</td>
                        <td className="px-4 py-3 text-gray-700">{lead.total_citas || 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openEditLead(lead)} className="inline-flex items-center rounded-md bg-gray-700 px-2 py-1 text-xs font-semibold text-white hover:bg-gray-800"><FaEdit className="mr-1" /> Editar</button>
                            <button onClick={() => setSelectedLead(lead)} className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"><FaComments className="mr-1" /> Notas</button>
                            <button onClick={() => handleDeleteLead(lead.id)} className="inline-flex items-center rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"><FaTrash className="mr-1" /> Eliminar</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Mostrando</span>
              <strong>{filteredLeads.length === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredLeads.length)}</strong>
              <span>de</span>
              <strong>{filteredLeads.length}</strong>
              <span>leads</span>
            </div>
            <div className="flex items-center gap-2">
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm">
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-40">Anterior</button>
              <span className="px-2 text-sm text-gray-700">{page} / {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-40">Siguiente</button>
            </div>
          </div>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            {pipelineStages.map((stage, index) => {
              const Icon = ICONS[stage.iconKey] || FaUserCheck;
              const palette = stagePalette(index);
              return (
                <div key={stage.key} className={`rounded-xl border p-2 ${palette.column}`}>
                  <div className="mb-2 flex items-center justify-between rounded-lg bg-white/70 px-2 py-2">
                    <p className="text-sm font-bold text-gray-800"><Icon className="inline mr-1" />{stage.key}<span className="ml-2 text-xs text-gray-500">{stage.probability}%</span></p>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-600">{kanbanColumns[stage.key]?.length || 0}</span>
                  </div>
                  <Droppable droppableId={stage.key}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="min-h-[380px] space-y-2">
                        {(kanbanColumns[stage.key] || []).map((lead, idx) => {
                          const score = computeLeadScore(lead);
                          return (
                            <Draggable key={lead.id} draggableId={String(lead.id)} index={idx}>
                              {(dragProvided) => (
                                <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps} className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                                  <p className="font-semibold text-gray-800">{lead.nombre || 'Sin nombre'}</p>
                                  <div className="mt-1"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${scoreBadgeClass(score)}`}><FaStar className="mr-1" />{score}</span></div>
                                  <p className="mt-1 text-xs text-gray-500">{lead.email || 'Sin email'}</p>
                                  <p className="text-xs text-gray-500">{lead.telefono || 'Sin teléfono'}</p>
                                  <div className="mt-3 flex gap-2">
                                    <button onClick={() => openEditLead(lead)} className="rounded bg-gray-700 px-2 py-1 text-xs font-semibold text-white hover:bg-gray-800">Editar</button>
                                    <button onClick={() => setSelectedLead(lead)} className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700">Notas</button>
                                    <button onClick={() => handleDeleteLead(lead.id)} className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700">Eliminar</button>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {showPipelineConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setShowPipelineConfig(false)}>
          <div className="w-full max-w-6xl rounded-2xl border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between gap-2">
              <h3 className="text-xl font-bold text-gray-800">Configurar Pipeline</h3>
              <button
                type="button"
                onClick={savePipelineConfig}
                className="inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold shadow-sm transition"
                style={{
                  backgroundColor: '#7c3aed',
                  color: '#ffffff',
                  border: '1px solid #6d28d9',
                  opacity: 1,
                  position: 'relative',
                  zIndex: 30,
                }}
              >
                Guardar pipeline
              </button>
            </div>
            <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50 p-3">
              <p className="mb-2 text-sm font-semibold text-indigo-900">Plantillas por negocio</p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5">
                {BUSINESS_PIPELINE_PRESETS.map((preset) => {
                  const PresetIcon = preset.icon || FaQuestionCircle;
                  const isActivePreset = selectedPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyBusinessPreset(preset.id)}
                      className={`rounded-lg border px-3 py-2 text-left transition ${
                        isActivePreset
                          ? 'border-indigo-400 bg-white shadow-sm'
                          : 'border-indigo-100 bg-indigo-50 hover:bg-white'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-800">
                        <PresetIcon className="mr-2 inline" />
                        {preset.name}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">{preset.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {pipelineDraft.map((stage, index) => {
                const Icon = ICONS[stage.iconKey] || FaUserCheck;
                const palette = stagePalette(index);
                return (
                  <div key={`draft-${index}`} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className={`rounded-md p-2 ${palette.column}`}><Icon className="text-slate-900" /></span>
                      <div className="flex gap-2">
                        <button onClick={() => movePipelineStage(index, -1)} className="rounded bg-white px-2 py-1 text-xs border"><FaChevronUp /></button>
                        <button onClick={() => movePipelineStage(index, 1)} className="rounded bg-white px-2 py-1 text-xs border"><FaChevronDown /></button>
                      </div>
                    </div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Nombre *</label>
                    <input
                      type="text"
                      value={stage.key}
                      onChange={(e) => {
                        const next = [...pipelineDraft];
                        next[index] = { ...next[index], key: e.target.value };
                        setPipelineDraft(next);
                      }}
                      className="mb-2 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm"
                    />
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Probabilidad (%) *</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={stage.probability}
                      onChange={(e) => {
                        const next = [...pipelineDraft];
                        next[index] = { ...next[index], probability: Number(e.target.value) };
                        setPipelineDraft(next);
                      }}
                      className="mb-2 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm"
                    />
                    <label className="mb-1 block text-xs font-semibold text-gray-600">Icono</label>
                    <select
                      value={stage.iconKey}
                      onChange={(e) => {
                        const next = [...pipelineDraft];
                        next[index] = { ...next[index], iconKey: e.target.value };
                        setPipelineDraft(next);
                      }}
                      className="mb-2 w-full rounded-lg border border-gray-300 px-2 py-1 text-sm"
                    >
                      {ICON_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <button
                      onClick={() => setPipelineDraft((prev) => prev.filter((_, i) => i !== index))}
                      className="rounded bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                      disabled={pipelineDraft.length <= 1}
                    >
                      Eliminar fase
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="sticky bottom-0 mt-4 flex flex-wrap justify-between gap-2 border-t border-gray-200 bg-white/95 pt-3">
              <button
                onClick={() => setPipelineDraft((prev) => [...prev, { key: `Fase ${prev.length + 1}`, probability: 50, iconKey: 'user-check' }])}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                + Añadir fase
              </button>
              <div className="flex gap-2">
                <Button onClick={() => setShowPipelineConfig(false)} variant="secondary">Cancelar</Button>
                <button
                  type="button"
                  onClick={savePipelineConfig}
                  className="inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold shadow-sm transition"
                  style={{
                    backgroundColor: '#7c3aed',
                    color: '#ffffff',
                    border: '1px solid #6d28d9',
                    opacity: 1,
                    position: 'relative',
                    zIndex: 30,
                  }}
                >
                  Guardar pipeline
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditLead && editLeadDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setShowEditLead(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-xl font-bold text-gray-800">Editar lead</h3>
            <div className="grid grid-cols-1 gap-3">
              <input type="text" value={editLeadDraft.nombre} onChange={(e) => setEditLeadDraft((prev) => ({ ...prev, nombre: e.target.value }))} placeholder="Nombre" className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <input type="email" value={editLeadDraft.email} onChange={(e) => setEditLeadDraft((prev) => ({ ...prev, email: e.target.value }))} placeholder="Email" className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <input type="text" value={editLeadDraft.telefono} onChange={(e) => setEditLeadDraft((prev) => ({ ...prev, telefono: e.target.value }))} placeholder="Teléfono" className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <select value={editLeadDraft.estado} onChange={(e) => setEditLeadDraft((prev) => ({ ...prev, estado: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                {stageKeys.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setShowEditLead(false)} variant="secondary">Cancelar</Button>
              <Button onClick={handleSaveLeadEdit} variant="primary">Guardar cambios</Button>
            </div>
          </div>
        </div>
      )}

      {showCreateLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setShowCreateLead(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-xl font-bold text-gray-800">Nuevo lead</h3>
            <div className="grid grid-cols-1 gap-3">
              <input type="text" placeholder="Nombre completo" value={newLead.nombre} onChange={(e) => setNewLead((prev) => ({ ...prev, nombre: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <input type="email" placeholder="Email" value={newLead.email} onChange={(e) => setNewLead((prev) => ({ ...prev, email: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <input type="text" placeholder="Teléfono" value={newLead.telefono} onChange={(e) => setNewLead((prev) => ({ ...prev, telefono: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <select value={newLead.estado} onChange={(e) => setNewLead((prev) => ({ ...prev, estado: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                {stageKeys.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button onClick={() => setShowCreateLead(false)} variant="secondary">Cancelar</Button>
              <Button onClick={handleCreateLead} variant="primary">Guardar lead</Button>
            </div>
          </div>
        </div>
      )}

      {selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" onClick={() => setSelectedLead(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{selectedLead.nombre || 'Lead sin nombre'}</h3>
                <p className="text-sm text-gray-500">{selectedLead.email || 'Sin email'}</p>
                <p className="text-sm text-gray-500">{selectedLead.telefono || 'Sin teléfono'}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${scoreBadgeClass(computeLeadScore(selectedLead))}`}>Score {computeLeadScore(selectedLead)}</span>
            </div>

            <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm">
              <p><strong>Estado:</strong> {normalizeLeadStatus(selectedLead.estado)}</p>
              <p><strong>Probabilidad etapa:</strong> {getStageProbability(selectedLead.estado)}%</p>
              <p><strong>Última cita:</strong> {selectedLead.ultima_cita ? new Date(selectedLead.ultima_cita).toLocaleDateString() : 'Sin citas'}</p>
              <p><strong>Total citas:</strong> {selectedLead.total_citas || 0}</p>
              <p><strong>Días como cliente:</strong> {selectedLead.dias_como_cliente || 0}</p>
              <p><strong>Días desde última cita:</strong> {selectedLead.dias_desde_ultima_cita || '-'}</p>
            </div>

            <div className="mt-4">
              <h4 className="mb-2 text-lg font-semibold text-gray-800">Notas</h4>
              {(!selectedLead.notas || selectedLead.notas.length === 0) && <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-sm text-gray-500">Aún no hay notas para este lead.</div>}
              <div className="space-y-2">
                {(selectedLead.notas || []).map((note, index) => {
                  const parsed = parseLeadNote(note);
                  return (
                    <div key={`${selectedLead.id}-note-${index}`} className="rounded-lg border border-gray-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm text-gray-700">{parsed.content}</p>
                        <button onClick={() => handleDeleteNote(index)} className="text-xs font-semibold text-red-600 hover:text-red-700">Eliminar</button>
                      </div>
                      {parsed.timestamp && <p className="mt-1 text-xs text-gray-400">{new Date(parsed.timestamp).toLocaleString()}</p>}
                    </div>
                  );
                })}
              </div>

              <textarea rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Añadir nota de seguimiento..." className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => setSelectedLead(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cerrar</button>
                <button onClick={handleAddNote} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Guardar nota</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMauticConfig && <MauticConfig isOpen={showMauticConfig} onClose={() => setShowMauticConfig(false)} />}
    </div>
  );
}

export default Leads;
